import {
  useCallback,
  useEffect,
  useState,
} from 'react';

import {
  fetchWithRetry,
} from '@/lib/apiRequest';

export interface GoogleCalendarIntegrationStatus {
  configured:
    boolean;

  missingConfiguration:
    string[];

  connected:
    boolean;

  status:
    string;

  scopes:
    string[];

  connectedAt:
    string | null;

  lastVerifiedAt:
    string | null;

  lastSyncAt:
    string | null;

  linkedEvents:
    number;

  syncedEvents:
    number;

  conflicts:
    number;

  errors:
    number;
}

export function useGoogleCalendarIntegration() {
  const [
    googleCalendar,
    setGoogleCalendar,
  ] =
    useState<
      GoogleCalendarIntegrationStatus |
      null
    >(null);

  const [
    isLoadingGoogleCalendar,
    setIsLoadingGoogleCalendar,
  ] =
    useState(true);

  const [
    isSyncingGoogleCalendar,
    setIsSyncingGoogleCalendar,
  ] =
    useState(false);

  const [
    isDisconnectingGoogleCalendar,
    setIsDisconnectingGoogleCalendar,
  ] =
    useState(false);

  const loadGoogleCalendarStatus =
    useCallback(
      async () => {
        setIsLoadingGoogleCalendar(
          true
        );

        try {
          const response =
            await fetchWithRetry(
              '/api/integrations/google-calendar/status'
            );

          const data =
            await response.json() as {
              success?: boolean;
              error?: string;
              googleCalendar?:
                GoogleCalendarIntegrationStatus;
            };

          if (
            !response.ok ||
            !data.success ||
            !data.googleCalendar
          ) {
            throw new Error(
              data.error ||
              'Não foi possível carregar o status do Google Calendar.'
            );
          }

          setGoogleCalendar(
            data.googleCalendar
          );

          return data.googleCalendar;
        } finally {
          setIsLoadingGoogleCalendar(
            false
          );
        }
      },
      []
    );

  const connectGoogleCalendar =
    useCallback(
      () => {
        window.location.assign(
          '/api/integrations/google-calendar/connect'
        );
      },
      []
    );

  const syncGoogleCalendar =
    useCallback(
      async () => {
        setIsSyncingGoogleCalendar(
          true
        );

        try {
          const response =
            await fetchWithRetry(
              '/api/integrations/google-calendar/sync',
              {
                method:
                  'POST',
              }
            );

          const data =
            await response.json() as {
              success?: boolean;
              error?: string;
            };

          if (
            !response.ok ||
            !data.success
          ) {
            throw new Error(
              data.error ||
              'Não foi possível sincronizar o Google Calendar.'
            );
          }

          await loadGoogleCalendarStatus();
        } finally {
          setIsSyncingGoogleCalendar(
            false
          );
        }
      },
      [
        loadGoogleCalendarStatus,
      ]
    );

  const disconnectGoogleCalendar =
    useCallback(
      async () => {
        setIsDisconnectingGoogleCalendar(
          true
        );

        try {
          const response =
            await fetchWithRetry(
              '/api/integrations/google-calendar/disconnect',
              {
                method:
                  'POST',
              }
            );

          const data =
            await response.json() as {
              success?: boolean;
              error?: string;
            };

          if (
            !response.ok ||
            !data.success
          ) {
            throw new Error(
              data.error ||
              'Não foi possível desconectar o Google Calendar.'
            );
          }

          await loadGoogleCalendarStatus();
        } finally {
          setIsDisconnectingGoogleCalendar(
            false
          );
        }
      },
      [
        loadGoogleCalendarStatus,
      ]
    );

  useEffect(
    () => {
      void loadGoogleCalendarStatus();
    },
    [
      loadGoogleCalendarStatus,
    ]
  );

  return {
    googleCalendar,

    isLoadingGoogleCalendar,
    isSyncingGoogleCalendar,
    isDisconnectingGoogleCalendar,

    loadGoogleCalendarStatus,
    connectGoogleCalendar,
    syncGoogleCalendar,
    disconnectGoogleCalendar,
  };
}